import { ArrowRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { SmartLink } from "../SmartLink";
import { CoinBadge } from "../CoinBadge";
import { useLivePrices, STABLES } from "../../hooks/useLivePrices";
import { IMG } from "./images";

export const AzWallet = () => {
  const coins = useLivePrices().filter((a) => !STABLES.includes(a.symbol)).slice(0, 12);
  return (
    <section className="az-wrap az-section az-split" data-testid="az-wallet">
      <div className="az-split-copy">
        <h2>Crypto Wallet</h2>
        <div className="az-coin-grid">
          {coins.map((c) => (
            <SmartLink key={c.symbol} href={`/trade?symbol=${c.symbol}_USDT`} className="az-coin-tile" data-testid={`az-wallet-coin-${c.symbol}`}>
              <CoinBadge logo={c.logo} cls={c.cls} icon={c.icon} symbol={c.symbol} size={38} />
              <span>{(c.name || c.symbol).toUpperCase()}</span>
            </SmartLink>
          ))}
        </div>
        <SmartLink className="az-link" href="/trade" data-testid="az-wallet-full-list">Full List of Assets <ArrowRight size={16} /></SmartLink>
      </div>
      <img className="az-split-img" src={IMG.wallet} alt="Imperio wallet" />
    </section>
  );
};

export const AzStats = ({ stats }) => {
  const items = [
    { k: "Today's total volume", v: stats?.volume, id: "volume" },
    { k: "Total registered users", v: stats?.users, id: "users" },
    { k: "Total assets listed", v: stats?.assets, id: "assets" },
    { k: "Days since platform launch", v: stats?.days != null ? String(stats.days) : null, id: "days" },
  ];
  return (
    <section className="az-wrap az-section az-stats" data-testid="az-stats">
      <h2>Platform Statistics</h2>
      <div className="az-stats-row">
        {items.map((s) => (
          <div key={s.id} className="az-stat"><b data-testid={`az-stat-${s.id}`}>{s.v ?? "…"}</b><span>{s.k}</span></div>
        ))}
      </div>
      <div className="az-stats-visual"><img src={IMG.trader} alt="Imperio trader" /></div>
    </section>
  );
};

export const AzReferral = ({ pct }) => (
  <section className="az-wrap az-section az-split az-split-ref" data-testid="az-referral">
    <div className="az-split-copy">
      <span className="az-overline">Affiliate program</span>
      <div className="az-ref-head">
        <b className="az-ref-pct" data-testid="az-referral-pct">{pct || "40%"}</b>
        <h2>Earn for referring users to Imperio!</h2>
      </div>
      <p>Did you know Imperio pays you when you refer other users? Log into your account and open the Referral section to find your personal link. Share it with friends, family and anyone else — for every trade they make, your account is credited automatically.</p>
      <SmartLink className="az-btn az-btn-primary" href="/referral" data-testid="az-referral-cta">Get Started</SmartLink>
    </div>
    <img className="az-split-img az-img-sm" src={IMG.referral} alt="Referral rewards" />
  </section>
);

const AppTile = ({ href, label, icon, id }) => (
  <a className={`az-app-tile${href ? "" : " soon"}`} href={href || undefined} target="_blank" rel="noreferrer" data-testid={`az-store-${id}`}>
    {icon}<span>{href ? label : `${label} · soon`}</span>
  </a>
);
const AppleIcon = () => <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor"><path d="M16.37 12.65c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.75 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.88.69 1.19-.02 1.94-1.08 2.67-2.14.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.31-.89-2.33-3.54zM14.17 6.18c.61-.74 1.02-1.76.91-2.78-.88.04-1.94.59-2.57 1.32-.56.65-1.06 1.69-.93 2.69.98.08 1.98-.5 2.59-1.23z" /></svg>;
const AndroidIcon = () => <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor"><path d="M17.6 9.48l1.84-3.18a.38.38 0 00-.66-.38l-1.86 3.22a11.4 11.4 0 00-9.84 0L5.22 5.92a.38.38 0 10-.66.38L6.4 9.48A10.8 10.8 0 001 18h22a10.8 10.8 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z" /></svg>;

export const AzApp = ({ apps }) => {
  const qr = apps?.android || apps?.ios || window.location.origin;
  return (
    <section id="az-app" className="az-wrap az-section az-split az-split-rev" data-testid="az-app">
      <img className="az-split-img" src={IMG.phones} alt="Imperio mobile app" />
      <div className="az-split-copy">
        <h2>Your wallet. Anywhere</h2>
        <p>All the power of the Imperio exchange in the palm of your hand. Download the Imperio crypto trading app today.</p>
        <p>App available and fully compatible with all of today's devices</p>
        <span className="az-scan">Scan to download</span>
        <div className="az-app-tiles">
          <div className="az-app-tile az-qr" data-testid="az-app-qr"><QRCodeSVG value={qr} size={104} bgColor="transparent" fgColor="#ffffff" /></div>
          <AppTile href={apps?.ios} label="iOS" icon={<AppleIcon />} id="ios" />
          <AppTile href={apps?.android} label="Android" icon={<AndroidIcon />} id="android" />
        </div>
      </div>
    </section>
  );
};

export const AzCta = () => (
  <section className="az-wrap az-cta" data-testid="az-cta">
    <div>
      <h2>Start trading<br />now</h2>
      <div className="az-hero-btns">
        <SmartLink className="az-btn az-btn-primary" href="/trade" data-testid="az-cta-start">Get Started</SmartLink>
        <SmartLink className="az-btn az-btn-ghost" href="/register" data-testid="az-cta-signup">Sign Up</SmartLink>
      </div>
    </div>
    <img src={IMG.bars} alt="" className="az-cta-img" />
  </section>
);
